import Button from '../../atoms/button';
import { Heading } from '../../typography/heading/Heading';
import { Text } from '../../typography/text/Text';
import ImageTeaser from '../imageTeaser';
import styles from './HeroBanner.module.scss';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMedia } from 'react-use';
import { useInView } from 'react-intersection-observer';
import { theme } from './_vars';
import { StylesCustomizer } from '../../molecules/stylesCustomizer';
import { TeaserButtons } from './TeaserButtons';
import Countdown from '../../atoms/countdown';
import { hexToRGBA } from '../../../utils/hexToRGBA';

const HeroBanner = ({ data }) => {
  const {
    label,
    title,
    subtitle,
    verticalPosition,
    titleAlignment,
    teasers,
    showOverlay,
    overlayTheme = 'dark',
    desktopHeight,
    mobileHeight,
    tabletHeight,
    boxWidth = 'wide',
    tasticType,
    buttonTitle,
    buttonType,
    buttonUrl,
    textColour,
    buttons,
    showVideoControls,
  } = data;

  const hasCountdown = !!data?.countdownTo;

  const titleBoxButtons = buttons?.length ? buttons : buttonUrl ? [{ buttonType, buttonUrl, buttonTitle }] : [];

  const cssHeightUnit = 'vh';

  const isSingleTeaser = teasers && Array.from(teasers).length === 1;

  const isMobile = useMedia('(max-width: 680px)', false);

  const { ref, inView } = useInView();

  const mainTeaser = useMemo(() => {
    return Array.from(teasers).find((item) => item.isMobileMain);
  }, [teasers]);

  const hasTeasersButtons = teasers.filter((item) => item.buttonUrl?.target || item.buttons.length).length > 0;

  const buttonShouldBeInverted = overlayTheme !== 'light' || !showOverlay;
  const textTheme = showOverlay && overlayTheme ? overlayTheme : 'dark';

  let singleAspectRatio;

  const renderButton = (teaser, index) => {
    if (!teaser?.buttonUrl || !teaser?.buttonUrl?.target) return null;

    return (
      <div key={`titleButton_${index}`} className={clsx(styles.buttonGroupSingle, styles[`tasticType_${tasticType}`])}>
        <div
          className={clsx(
            styles.buttonHolder,
            styles[verticalPosition ? `verticalPosition__${verticalPosition}` : 'verticalPosition__center'],
            styles[`tasticType_${tasticType}`],
          )}
        >
          <Button
            variant={teaser?.isMobileBackgroundOnly && isMobile ? 'secondary' : teaser.buttonType}
            shape="square"
            isOutlined={false}
            as={teaser.buttonType === 'text' ? 'text' : 'link'}
            nodeLink={teaser.buttonUrl}
            ariaLabel={teaser.buttonTitle}
            isInverted={buttonShouldBeInverted}
            className={clsx(
              styles.heroButton,
              styles[teaser?.isMobileBackgroundOnly && isMobile ? 'secondary' : teaser.buttonType],
              { [styles.inverted]: buttonShouldBeInverted },
            )}
          >
            {teaser.buttonTitle}
          </Button>
        </div>
      </div>
    );
  };

  const LabelText = (variables) => {
    return (
      <Text
        align={titleAlignment}
        className={clsx(styles.heroLabel, styles[`contentBoxWidth_${boxWidth}`], styles[`text_${textTheme}`], {
          [styles[`text_${overlayTheme}`]]: showOverlay,
        })}
        as="span"
        inlineStyles={{ color: textColour }}
        variables={variables}
        weight={theme === 'Jag' ? 'semibold' : 'normal'}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: label,
          }}
        />
      </Text>
    );
  };
  const TitleText = (variables) => {
    return (
      <Heading
        className={clsx(styles.teaserHeader, styles[`contentBoxWidth_${boxWidth}`], styles[`text_${textTheme}`], {
          [styles[`header_${overlayTheme}`]]: showOverlay,
        })}
        align={titleAlignment}
        as="h2"
        styleAs={tasticType === 'limited' ? 'short_h1' : 'h1'}
        placement="hero"
        variables={variables}
        inlineStyles={{ color: textColour }}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: title,
          }}
        />
      </Heading>
    );
  };
  const SubTitleText = (variables) => {
    return (
      <Text
        align={titleAlignment}
        className={clsx(
          styles.heroLabel,
          styles.subtitle,
          styles[`contentBoxWidth_${boxWidth}`],
          styles[`text_${textTheme}`],
          {
            [styles[`text_${overlayTheme}`]]: showOverlay,
          },
        )}
        as="span"
        weight={theme === 'Jag' ? 'semibold' : 'normal'}
        variables={variables}
        inlineStyles={{ color: textColour }}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: subtitle,
          }}
        />
      </Text>
    );
  };

  const StyledLabel = StylesCustomizer(LabelText);
  const StyledTitle = StylesCustomizer(TitleText);
  const StyledSubTitle = StylesCustomizer(SubTitleText);

  const [height, setHeight] = useState(0);
  const titleRef = useRef(null);

  useEffect(() => {
    if (titleRef?.current && !height) {
      if (!height || height !== titleRef.current.clientHeight) {
        setTimeout(() => {
          setHeight(titleRef.current.clientHeight);
        }, 600);
      }
    }
  }, [titleRef, tasticType, boxWidth, hasTeasersButtons, titleBoxButtons]);

  const heroTitle = (label || title) && (
    <div
      ref={titleRef}
      className={clsx(
        styles.heroTitleWrapper,
        [styles[`containerWidth_${boxWidth}`]],
        styles[verticalPosition && `verticalPosition__${verticalPosition}`],
        {
          [styles.heroTitleWrapperShort]: tasticType === 'limited',
          [styles.hasCountdown]: hasCountdown,
          [styles.heroTitleBackground]: hasCountdown && data.countdownBackground,
          [styles[`heroTitlePosition_${data.countdownPosition}`]]: hasCountdown && data.countdownPosition,
        },
      )}
      style={{
        '--title-block-background': data.countdownBackground
          ? hexToRGBA(data.countdownBackground, data.countdownBackgroundOpacity / 100)
          : '',
      }}
    >
      <div
        className={clsx(
          styles.heroTitle,
          styles[`verticalPosition__${verticalPosition}`],
          styles[`textAlignment__${titleAlignment}`],
        )}
      >
        {label && <StyledLabel styles={data?.label_styles} />}
        {title && <StyledTitle styles={data?.title_styles} />}
        {subtitle && <StyledSubTitle styles={data?.subtitle_styles} />}
        {hasCountdown && (
          <Countdown
            viewAs="block"
            countdownTo={data?.countdownTo}
            background={data?.countdownBackground}
            numberBackground={data?.countNumberBackground}
            numberOpacity={data?.countNumberOpacity}
            numberColor={data?.countNumberColor}
            textColor={data?.countTextColor}
          />
        )}
      </div>
      {tasticType !== 'limited' ? (
        hasTeasersButtons && (
          <TeaserButtons
            buttonShouldBeInverted={buttonShouldBeInverted}
            teasers={teasers}
            renderButton={renderButton}
          />
        )
      ) : (
        <div
          className={
            titleBoxButtons?.length > 0
              ? clsx(styles.buttonHolder, styles.buttonBottom, styles[`verticalPosition__center`])
              : ''
          }
        >
          {titleBoxButtons?.map((btn, index) => renderButton(btn, index))}
        </div>
      )}
      {(tasticType === 'extended' || (hasCountdown && tasticType !== 'limited')) && titleBoxButtons.length > 0 && (
        <div
          className={clsx(
            styles.buttonHolder,
            styles.buttonBottom,
            styles[`verticalPosition__center`],
            styles[`tasticType_${tasticType}`],
          )}
        >
          {titleBoxButtons.map((btn, index) => {
            if (!btn?.buttonUrl || !btn?.buttonUrl?.target) return null;
            return (
              <Button
                key={`btn_${index}`}
                variant="primary"
                shape="square"
                isOutlined={false}
                as={btn.buttonType === 'text' ? 'text' : 'link'}
                nodeLink={btn.buttonUrl}
                isInverted={buttonShouldBeInverted}
                className={clsx(
                  styles.heroButton,
                  styles[mainTeaser && mainTeaser.isMobileBackgroundOnly && isMobile ? '' : btn.buttonType],
                  { [styles.inverted]: buttonShouldBeInverted },
                )}
              >
                <span
                  className={buttonType === 'text' ? styles.buttonBottomLink : ''}
                  style={buttonType === 'text' ? { color: textColour } : {}}
                >
                  {btn.buttonTitle}
                </span>
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );

  const heroTitleButtonsOnly =
    !label && !title && hasTeasersButtons ? (
      <div
        className={clsx(styles.heroTitleWrapper, [
          styles[`containerWidth_${boxWidth}`],
          styles[`tasticType_${tasticType}`],
        ])}
      >
        <TeaserButtons buttonShouldBeInverted={buttonShouldBeInverted} teasers={teasers} renderButton={renderButton} />
      </div>
    ) : null;

  return (
    <div
      ref={ref}
      className={clsx(styles.heroWrapper, styles[`verticalPosition__${verticalPosition}`])}
      style={{ '--teaser-min-height': `${height}px` }}
    >
      {heroTitle || heroTitleButtonsOnly}
      <div className={isSingleTeaser ? styles.teasersWrapperSingle : styles.teasersWrapper}>
        {Array.from(teasers).map((teaser, index) => {
          const mergedVideoMobile =
            teaser.videoUrlMobile ||
            (teaser.imageMobile?.media?.resourceType === 'video' && teaser.imageMobile?.media?.file) ||
            teaser.videoUrl ||
            (teaser.image?.media?.resourceType === 'video' && teaser.image?.media?.file);
          const mergedImageMobile = teaser.imageMobile || teaser.image;
          const isLinkCovered = teaser?.buttons?.find((btn) => btn.isClickableTeaser);
          return (
            <ImageTeaser
              preloadImage={inView}
              isMobile={isMobile}
              isMain={teaser.isMobileMain}
              spacing={teaser.spacing ? teaser.spacing : 'none'}
              background={teaser.backgroundColor && teaser.backgroundColor}
              image={teaser.image}
              imageMobile={mergedImageMobile}
              videoUrl={teaser.videoUrl}
              videoUrlMobile={mergedVideoMobile}
              hideImageOnMobile={isSingleTeaser ? false : teaser.isMobileBackgroundOnly}
              singleAspectRatio={isSingleTeaser ? singleAspectRatio : null}
              isOverlay={showOverlay}
              overlayTheme={overlayTheme}
              hideOnMobile={isSingleTeaser ? false : !teaser.isMobileMain}
              customClasses={{
                imageSpacing__large: styles.imageSpacing__large,
                contentPaddings: isSingleTeaser ? styles.contentPaddings : null,
              }}
              heightStyles={{
                '--desktop-height': desktopHeight + cssHeightUnit,
                '--mobile-height': mobileHeight ? mobileHeight + cssHeightUnit : desktopHeight + cssHeightUnit,
                '--tablet-height': tabletHeight ? tabletHeight + cssHeightUnit : desktopHeight + cssHeightUnit,
              }}
              key={`teaser_${index}`}
              isLinkCovered={!!isLinkCovered}
              link={isLinkCovered?.buttonUrl}
              showVideoControls={showVideoControls}
            />
          );
        })}
      </div>
    </div>
  );
};

HeroBanner.propTypes = {
  data: PropTypes.object.isRequired,
};

export default HeroBanner;
